import React, { InputHTMLAttributes, TextareaHTMLAttributes, SelectHTMLAttributes, ReactNode } from 'react';

interface BaseProps {
  label?: string;
  error?: string;
  hint?: string;
}

// Text Input
interface FormInputProps extends BaseProps, Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> { }

export function FormInput({ label, error, hint, ...props }: FormInputProps) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="text-sm font-medium text-gray-300">{label}</label>
      )}
      <input
        {...props}
        className={`px-3 py-2.5 bg-bg-form border rounded-lg text-sm text-gray-50 placeholder-gray-500 focus:outline-none focus:border-primary transition-colors ${error ? 'border-red-500' : 'border-border-subtle hover:border-border-default'
          }`}
      />
      {hint && !error && (
        <p className="text-xs text-gray-400">{hint}</p>
      )}
      {error && (
        <p className="text-xs text-red-400">{error}</p>
      )}
    </div>
  );
}

// Textarea
interface FormTextareaProps extends BaseProps, Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> { }

export function FormTextarea({ label, error, hint, ...props }: FormTextareaProps) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="text-sm font-medium text-gray-300">{label}</label>
      )}
      <textarea
        {...props}
        className={`px-3 py-2.5 bg-bg-form border rounded-lg text-sm text-gray-50 placeholder-gray-500 focus:outline-none focus:border-primary transition-colors resize-none ${error ? 'border-red-500' : 'border-border-subtle hover:border-border-default'
          }`}
      />
      {hint && !error && (
        <p className="text-xs text-gray-400">{hint}</p>
      )}
      {error && (
        <p className="text-xs text-red-400">{error}</p>
      )}
    </div>
  );
}

// Select
interface SelectOption {
  value: string;
  label: string;
}

interface FormSelectProps extends BaseProps, Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  options: SelectOption[];
  placeholder?: string;
}

export function FormSelect({ label, error, hint, options, placeholder, ...props }: FormSelectProps) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="text-sm font-medium text-gray-300">{label}</label>
      )}
      <select
        {...props}
        className={`px-3 py-2.5 bg-bg-form border rounded-lg text-sm text-gray-50 focus:outline-none focus:border-primary transition-colors appearance-none cursor-pointer ${error ? 'border-red-500' : 'border-border-subtle hover:border-border-default'
          }`}
      >
        {placeholder && (
          <option value="" className="text-gray-500">{placeholder}</option>
        )}
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {hint && !error && (
        <p className="text-xs text-gray-400">{hint}</p>
      )}
      {error && (
        <p className="text-xs text-red-400">{error}</p>
      )}
    </div>
  );
}

// Button
interface FormButtonProps {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit';
  onClick?: () => void;
}

export function FormButton({
  children,
  variant = 'primary',
  size = 'md',
  loading,
  disabled,
  type = 'button',
  onClick
}: FormButtonProps) {
  const baseClasses = 'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  const variantClasses = {
    primary: 'bg-primary hover:bg-primary/90 text-white',
    secondary: 'border border-border-default hover:bg-bg-elevated text-gray-400 hover:text-gray-50',
    ghost: 'text-gray-400 hover:text-gray-50 hover:bg-bg-elevated'
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-sm'
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]}`}
    >
      {loading && (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      )}
      {children}
    </button>
  );
}
